import { Avatar, CSSObject, styled, Theme } from '@mui/material'
import { ReactNode } from 'react'

export type ThumbnailSize = 'xSmall' | 'small' | 'medium' | 'large' | 'xLarge'

export type ThumbnailColor = 'subtle' | 'surface' | 'info' | 'success' | 'error' | 'warning' | 'brand'

export type ThumbnailVariant = 'flat' | 'elevated'

export type ThumbnailProps = {
  size?: ThumbnailSize
  color?: ThumbnailColor
  variant?: ThumbnailVariant
  src?: string
  'data-testid'?: string
  children: ReactNode
}

const SIZES = {
  xSmall: { container: 24, radius: 'radius-xs', icon: 16 },
  small: { container: 28, radius: 'radius-md', icon: 18 },
  medium: { container: 36, radius: 'radius-md', icon: 18 },
  large: { container: 40, radius: 'radius-xl', icon: 18 },
  xLarge: { container: 48, radius: 'radius-xl', icon: 24 },
} as const satisfies Record<ThumbnailSize, unknown>

const colorStyles = ({ palette }: Theme): Record<ThumbnailColor, CSSObject> => ({
  subtle: { backgroundColor: palette.bg.surface.subtle, color: palette.fg.default },
  surface: { backgroundColor: palette.bg.surface.default, color: palette.fg.default },
  info: { backgroundColor: palette.bg.info.muted, color: palette.fg.info.onInfoMuted },
  success: { backgroundColor: palette.bg.success.muted, color: palette.fg.success.onSuccessMuted },
  error: { backgroundColor: palette.bg.danger.muted, color: palette.fg.danger.onDangerMuted },
  warning: { backgroundColor: palette.bg.warning.muted, color: palette.fg.warning.onWarningMuted },
  brand: { backgroundColor: palette.bg.brand.muted, color: palette.fg.brand.onBrandMuted },
})

const ThumbnailRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'color' && prop !== 'variant',
})<{ size: ThumbnailSize; color: ThumbnailColor; variant: ThumbnailVariant }>(({ theme, size, color, variant }) => {
  const { container, radius, icon } = SIZES[size]
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: container,
    height: container,
    borderRadius: theme.shape[radius],
    boxShadow: variant === 'elevated' ? theme.customShadows.xs : 'none',
    ...colorStyles(theme)[color],
    '& > svg:not(.MuiSvgIcon-root), & .MuiAvatar-root > svg:not(.MuiSvgIcon-root)': {
      width: icon,
      height: icon,
    },
    '& > .MuiSvgIcon-root, & .MuiAvatar-root > .MuiSvgIcon-root': {
      fontSize: icon,
    },
    // Avatar owns the image and swaps in its children when the image cannot be loaded; the
    // container keeps its own colour so the fallback icon sits on the usual surface.
    '& > .MuiAvatar-root': {
      width: '100%',
      height: '100%',
      backgroundColor: 'transparent',
      color: 'inherit',
    },
    '& .MuiAvatar-img': {
      objectFit: 'contain',
    },
    '& > .MuiCircularProgress-root': {
      width: `${icon}px !important`, // styles are set inline by the component so have to do this for specificity
      height: `${icon}px !important`,
    },
  }
})

export const Thumbnail = ({
  size = 'xLarge',
  color = 'subtle',
  variant = 'flat',
  src,
  'data-testid': dataTestId,
  children,
}: ThumbnailProps) => (
  <ThumbnailRoot size={size} color={color} variant={variant} data-testid={dataTestId}>
    {src == null ? (
      children
    ) : (
      <Avatar src={src} alt="" variant="square" slotProps={{ img: { referrerPolicy: 'no-referrer' } }}>
        {children}
      </Avatar>
    )}
  </ThumbnailRoot>
)
